import { IStyleConfig, mergeStyles } from '@cian/utils';
import * as React from 'react';
import { connect } from 'react-redux';

import styles from './video.css';
import { CloseIcon, PlayIcon } from '../../../common/icons';
import { getVersion, isAppleDevice, isWinPhone } from '../../popup_gallery/device_detector';
import {
  createYoutubePlayer,
  getYoutubeID,
  getYoutubeVideoPreviewUrl,
  isYouTube,
  IYouTubeEvent,
  PlayerState,
  VIDEO_NOT_AVAILABLE_COMBINATION,
} from '../youtube';

interface IConnectedProps {
  userAgent: string;
}

interface IOwnProps {
  video: string;
  styles?: IStyleConfig;
  iframeStyles?: IStyleConfig;
  previewStyles?: IStyleConfig;
  hideCloseButton?: boolean;
  onCloseClick?(e: React.MouseEvent<HTMLElement>): void;
  onPlayerStateChange?(state: PlayerState): void;
}

type VideoProps = IOwnProps & IConnectedProps;

interface IVideoState {
  yt_id?: string;
  playing: boolean;
  notAvailable: boolean;
}

export class Video extends React.PureComponent<VideoProps, IVideoState> {
  public static defaultProps = {
    onCloseClick: () => {},
    hideCloseButton: false,
    userAgent: '',
  };

  public state = {
    yt_id: '',
    playing: false,
    notAvailable: false,
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  private player: any;
  private stateHistory: PlayerState[] = [];

  public UNSAFE_componentWillMount() {
    const { video } = this.props;

    this.setState({
      yt_id: `yt-player-${getYoutubeID(video)}-${Math.round(Math.random() * 1000)}`,
    });
  }

  public componentDidMount() {
    createYoutubePlayer().then(this.initializeVideo).catch();
  }

  public render() {
    const { video, hideCloseButton, userAgent } = this.props;

    // only YouTube links allowed
    if (!isYouTube(video)) {
      return null;
    }

    // для некоторых девайсов введен запрет на программный запуск видео -
    // для них мы убираем превью, чтобы юзер нажимал сразу на видео для его запуска
    // http://stackoverflow.com/questions/12496144/can-you-autoplay-html5-videos-on-the-ipad
    const safariVersion = getVersion(userAgent, 'Safari');
    const noPreview =
      safariVersion < 10 || isWinPhone(userAgent) || (isAppleDevice(userAgent) && this.state.notAvailable);

    const containerStyles = mergeStyles(styles['container'], this.props.styles);
    const iframeStyles = mergeStyles(styles['video'], this.props.iframeStyles);
    const previewStyles = mergeStyles(
      styles['preview'],
      noPreview && styles['hidden_preview'],
      !noPreview ? { backgroundImage: `url(${getYoutubeVideoPreviewUrl(video)})` } : {},
      this.props.previewStyles,
    );

    const closeButton = !hideCloseButton && (
      <div onClick={this.handleClose} className={styles['close']}>
        <CloseIcon />
      </div>
    );

    const preview = !noPreview && (
      <div {...previewStyles} onClick={this.playVideo}>
        <div className={styles['play']}>
          <PlayIcon />
        </div>
      </div>
    );

    const { playing } = this.state;

    return (
      <div {...containerStyles}>
        <div {...mergeStyles(!playing && styles['controls--preview'])}>{playing ? closeButton : preview}</div>
        <div
          {...iframeStyles}
          id={this.state.yt_id}
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        />
      </div>
    );
  }

  private handleClose = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    this.stopVideo();

    if (this.props.onCloseClick) {
      this.props.onCloseClick(e);
    }
  };

  private initializeVideo = () => {
    // oxlint-disable-next-line typescript/no-explicit-any
    this.player = new (window as any).YT.Player(this.state.yt_id, {
      // tslint:disable-line:no-any
      videoId: getYoutubeID(this.props.video),
      playerVars: {
        modestbranding: 1,
        showinfo: 0,
        autohide: 1,
        rel: 0,
      },
      events: {
        onStateChange: this.handlePlayerStateChange,
      },
    });
  };

  private handlePlayerStateChange = (event: IYouTubeEvent) => {
    if (this.props.onPlayerStateChange) {
      this.props.onPlayerStateChange(event.data);
    }
    const newState: Partial<IVideoState> = {};

    newState.notAvailable = false;
    if (
      ![PlayerState.NOT_PLAYED, PlayerState.PLAYING, PlayerState.PAUSED, PlayerState.BUFFERING].includes(event.data) &&
      this.state.playing
    ) {
      newState.playing = false;
    }

    this.stateHistory.push(event.data);
    if (this.stateHistory.slice(-3).toString() === VIDEO_NOT_AVAILABLE_COMBINATION.toString()) {
      newState.notAvailable = true;
      this.stateHistory = [];
    }

    this.setState(newState as IVideoState);
  };

  private playVideo = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (typeof this.player.playVideo === 'function') {
      this.setState(
        {
          playing: true,
        },
        () => {
          this.player.playVideo();
        },
      );
    }
  };

  private stopVideo = () => {
    this.setState({
      playing: false,
    });

    this.player.stopVideo();
  };
}

function mapStateToProps(): IConnectedProps {
  return {
    userAgent: (navigator && navigator.userAgent) || '',
  };
}

export const VideoContainer = connect<IConnectedProps, {}, IOwnProps>(mapStateToProps)(Video);
